import { ArchiveRestore, CalendarDays, Edit3, Eye, Image, Plus, Search, Send, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { createNewsExcerpt, fetchAdminNews, importArchivePosts } from "../../lib/content";
import { supabase } from "../../lib/supabase";
import { useAdminToast } from "../../context/AdminToastContext";
import { useAuth } from "../../context/AuthContext";
import { adminErrorMessage, missingFieldsMessage } from "../../lib/adminFeedback";
import { AdminConfirmDialog } from "../../components/AdminConfirmDialog";
import { MarkdownEditor } from "../../components/MarkdownEditor";
import { NewsStatusSelect } from "../../components/NewsStatusSelect";
import { writeAuditLog } from "../../lib/audit";
import type { NewsPost, NewsStatus } from "../../types";
import { AdminPageSkeleton } from "../../components/LoadingSkeleton";

const emptyPost = (): NewsPost => ({
  id: crypto.randomUUID(),
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  cover_image: null,
  images: [],
  status: "draft",
  published_at: new Date().toISOString().slice(0, 16),
});

interface StoredNewsDraft {
  editor: NewsPost;
  slugManuallyEdited: boolean;
  savedAt: string;
}

const NEWS_DRAFT_STORAGE_PREFIX = "ttc-admin-news-editor-draft-v1";

function readStoredNewsDraft(storageKey: string): StoredNewsDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredNewsDraft>;
    const editor = stored.editor as Partial<NewsPost> | undefined;
    if (
      !editor
      || typeof editor.id !== "string"
      || typeof editor.slug !== "string"
      || typeof editor.title !== "string"
      || typeof editor.body !== "string"
      || typeof editor.published_at !== "string"
      || !["draft", "published", "archived"].includes(String(editor.status))
    ) return null;
    return {
      editor: editor as NewsPost,
      slugManuallyEdited: Boolean(stored.slugManuallyEdited),
      savedAt: typeof stored.savedAt === "string" ? stored.savedAt : new Date().toISOString(),
    };
  }
  catch {
    return null;
  }
}

function toSlug(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/ß/g, "ss").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function AdminNewsPage() {
  const { user } = useAuth();
  const { showToast } = useAdminToast();
  const [params, setParams] = useSearchParams();
  const draftStorageKey = `${NEWS_DRAFT_STORAGE_PREFIX}:${user?.id || "unknown"}`;
  const [restoredDraft] = useState<StoredNewsDraft | null>(() => readStoredNewsDraft(draftStorageKey));
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | NewsStatus>("all");
  const [editor, setEditor] = useState<NewsPost | null>(() => restoredDraft?.editor || null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(() => restoredDraft?.slugManuallyEdited || false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<NewsPost | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setPosts(await fetchAdminNews()); }
    catch (error) { showToast({ type: "error", title: "Beiträge nicht geladen", message: adminErrorMessage(error, "Die Beiträge konnten nicht geladen werden.") }); }
  };
  useEffect(() => { void load().finally(() => setLoading(false)); }, []);
  useEffect(() => {
    if (!restoredDraft) return;
    showToast({
      type: "info",
      title: "Entwurf wiederhergestellt",
      message: restoredDraft.editor.title.trim()
        ? `Deine nicht gespeicherten Änderungen an „${restoredDraft.editor.title.trim()}“ sind wieder da.`
        : "Dein nicht gespeicherter neuer Beitrag ist wieder geöffnet.",
    });
  }, []);
  useEffect(() => {
    try {
      if (!editor) {
        window.sessionStorage.removeItem(draftStorageKey);
        return;
      }
      window.sessionStorage.setItem(draftStorageKey, JSON.stringify({
        editor,
        slugManuallyEdited,
        savedAt: new Date().toISOString(),
      } satisfies StoredNewsDraft));
    }
    catch {
      // The editor remains usable if browser storage is unavailable.
    }
  }, [draftStorageKey, editor, slugManuallyEdited]);
  useEffect(() => { if (params.get("new") === "1") { setSlugManuallyEdited(false); setEditor(emptyPost()); setParams({}, { replace: true }); } }, [params, setParams]);

  const filtered = useMemo(() => posts.filter((post) => (filter === "all" || post.status === filter) && `${post.title} ${post.excerpt}`.toLowerCase().includes(query.toLowerCase())), [posts, filter, query]);

  if (loading) return <AdminPageSkeleton variant="table" />;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editor) return;
    const missing = [
      !editor.title.trim() && "Titel",
      !editor.body.trim() && "Inhalt",
      !editor.published_at && "Datum",
    ].filter((field): field is string => Boolean(field));
    if (missing.length > 0) {
      showToast({ type: "warning", title: "Angaben fehlen", message: missingFieldsMessage(missing) });
      return;
    }
    if (Number.isNaN(new Date(editor.published_at).getTime())) {
      showToast({ type: "warning", title: "Datum ungültig", message: "Bitte wähle ein gültiges Veröffentlichungsdatum aus." });
      return;
    }
    setBusy(true);
    const previousPost = posts.find((post) => post.id === editor.id);
    const row = { ...editor, excerpt: createNewsExcerpt(editor.body), slug: editor.slug || toSlug(editor.title), published_at: new Date(editor.published_at).toISOString(), updated_at: new Date().toISOString() };
    delete row.source;
    const { error } = await supabase.from("news_posts").upsert(row);
    if (error) showToast({ type: "error", title: "Speichern fehlgeschlagen", message: adminErrorMessage(error, "Der Beitrag konnte nicht gespeichert werden.") });
    else {
      const action = !previousPost
        ? "news.created"
        : previousPost.status !== editor.status
          ? editor.status === "published" ? "news.published" : editor.status === "archived" ? "news.archived" : "news.unpublished"
          : "news.updated";
      await writeAuditLog({ action, entityType: "news", entityId: editor.id, entityTitle: editor.title.trim(), details: { slug: row.slug, status: editor.status, previousStatus: previousPost?.status || null } });
      setEditor(null);
      setSlugManuallyEdited(false);
      showToast({
        type: "success",
        title: editor.status === "published" ? "Beitrag veröffentlicht" : editor.status === "archived" ? "Beitrag archiviert" : "Entwurf gespeichert",
        message: `„${editor.title.trim()}“ wurde erfolgreich gespeichert.`,
      });
      await load();
    }
    setBusy(false);
  };

  const remove = async (post: NewsPost) => {
    setDeleteBusy(true);
    const { error } = await supabase.from("news_posts").delete().eq("id", post.id);
    if (error) showToast({ type: "error", title: "Löschen fehlgeschlagen", message: adminErrorMessage(error, "Der Beitrag konnte nicht gelöscht werden.") });
    else {
      await writeAuditLog({ action: "news.deleted", entityType: "news", entityId: post.id, entityTitle: post.title, details: { slug: post.slug, status: post.status } });
      setPendingDelete(null);
      showToast({ type: "success", title: "Beitrag gelöscht", message: `„${post.title}“ wurde entfernt.` });
      await load();
    }
    setDeleteBusy(false);
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const count = await importArchivePosts();
      if (count > 0) await writeAuditLog({ action: "news.imported", entityType: "news", entityTitle: "Beitragsarchiv", details: { count } });
      showToast({ type: count > 0 ? "success" : "info", title: count > 0 ? "Archiv importiert" : "Keine Beiträge gefunden", message: count > 0 ? `${count} Beiträge sind jetzt im Adminbereich bearbeitbar.` : "Im Archiv waren keine importierbaren Beiträge vorhanden." });
      await load();
    }
    catch (error) { showToast({ type: "error", title: "Import fehlgeschlagen", message: adminErrorMessage(error, "Die Archivbeiträge konnten nicht importiert werden.") }); }
    finally { setBusy(false); }
  };

  const uploadCover = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;
    if (!file.type.startsWith("image/")) { showToast({ type: "warning", title: "Falscher Dateityp", message: "Bitte wähle eine Bilddatei aus." }); event.target.value = ""; return; }
    if (file.size > 8 * 1024 * 1024) { showToast({ type: "warning", title: "Bild zu groß", message: "Das Bild darf höchstens 8 MB groß sein." }); event.target.value = ""; return; }
    setUploading(true);
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `news/${new Date().getFullYear()}/${crypto.randomUUID()}.${extension}`;
    const { error } = await supabase.storage.from("media").upload(path, file, { cacheControl: "31536000", upsert: false });
    if (error) showToast({ type: "error", title: "Upload fehlgeschlagen", message: adminErrorMessage(error, "Das Titelbild konnte nicht hochgeladen werden.") });
    else {
      const { data } = supabase.storage.from("media").getPublicUrl(path);
      setEditor({ ...editor, cover_image: data.publicUrl });
      showToast({ type: "success", title: "Titelbild hochgeladen", message: "Das neue Bild ist ausgewählt. Speichere den Beitrag, um die Änderung zu übernehmen." });
    }
    setUploading(false);
    event.target.value = "";
  };

  return (
    <>
      <div className="admin-page-heading"><div><span>Redaktion</span><h1>News verwalten</h1><p>Beiträge erstellen, bearbeiten und gezielt veröffentlichen.</p></div><button className="admin-primary-button" onClick={() => { setSlugManuallyEdited(false); setEditor(emptyPost()); }}><Plus /> Neuer Beitrag</button></div>
      <section className="admin-panel news-admin-panel">
        <div className="admin-list-toolbar"><div className="admin-tabs"><button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>Alle <span>{posts.length}</span></button><button className={filter === "published" ? "active" : ""} onClick={() => setFilter("published")}>Veröffentlicht</button><button className={filter === "draft" ? "active" : ""} onClick={() => setFilter("draft")}>Entwürfe</button><button className={filter === "archived" ? "active" : ""} onClick={() => setFilter("archived")}>Archiviert</button></div><div className="admin-toolbar-actions"><label className="admin-search"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Suchen…" /></label><button className="admin-secondary-button" onClick={() => void runImport()} disabled={busy}><ArchiveRestore /> Archiv importieren</button></div></div>
        <div className="admin-news-table"><div className="table-head"><span>Beitrag</span><span>Status</span><span>Datum</span><span>Aktionen</span></div>{filtered.map((post) => <div className="table-row" key={post.id}><div className="post-cell"><img src={post.cover_image || "/images/hero-table-tennis.png"} alt="" /><div><strong>{post.title}</strong><small>{post.excerpt || "Noch kein Haupttext"}</small></div></div><div><span className={`status status--${post.status}`}>{post.status === "published" ? "Veröffentlicht" : post.status === "archived" ? "Archiviert" : "Entwurf"}</span></div><div className="date-cell"><CalendarDays /> {new Date(post.published_at).toLocaleDateString("de-DE")}</div><div className="table-actions">{post.status === "published" && <a href={`/aktuelles/${post.slug}`} target="_blank" aria-label="Ansehen"><Eye /></a>}<button onClick={() => { setSlugManuallyEdited(true); setEditor(post); }} aria-label="Bearbeiten"><Edit3 /></button><button className="danger" onClick={() => setPendingDelete(post)} aria-label="Löschen"><Trash2 /></button></div></div>)}{filtered.length === 0 && <div className="admin-empty"><Search /><p>Keine passenden Beiträge gefunden.</p></div>}</div>
      </section>
      {editor && (
        <div className="editor-overlay" role="dialog" aria-modal="true">
          <div className="editor-panel">
            <div className="editor-header">
              <div>
                <span>{posts.some((post) => post.id === editor.id) ? "Beitrag bearbeiten" : "Neuer Beitrag"}</span>
                <h2>{editor.title || "Unbenannter Beitrag"}</h2>
              </div>
              <button onClick={() => { setEditor(null); setSlugManuallyEdited(false); }}><X /></button>
            </div>
            <form onSubmit={save} noValidate className="editor-form">
              <fieldset className="editor-fieldset">
                <div className="editor-content">
                  <div className="editor-main">
                    <label>
                      Titel
                      <input
                        value={editor.title}
                        onChange={(event) => {
                          const title = event.target.value;
                          setEditor({ ...editor, title, slug: slugManuallyEdited ? editor.slug : toSlug(title) });
                        }}
                        placeholder="Aussagekräftiger Beitragstitel"
                      />
                    </label>
                    <MarkdownEditor value={editor.body} onChange={(body) => setEditor({ ...editor, body })} placeholder="Schreibe hier den vollständigen Beitrag…" />
                  </div>
                  <aside className="editor-aside">
                    <div className="editor-card">
                      <h3>Veröffentlichung</h3>
                      <div className="status-field"><span>Status</span><NewsStatusSelect value={editor.status} onChange={(status) => setEditor({ ...editor, status })} /></div>
                      <label>
                        Datum
                        <input type="datetime-local" value={editor.published_at.slice(0, 16)} onChange={(event) => setEditor({ ...editor, published_at: event.target.value })} />
                      </label>
                      <label>
                        URL-Kürzel
                        <input
                          value={editor.slug}
                          onChange={(event) => {
                            setSlugManuallyEdited(true);
                            setEditor({ ...editor, slug: toSlug(event.target.value) });
                          }}
                        />
                        <small>{slugManuallyEdited ? "Individuell festgelegt" : "Wird automatisch aus dem Titel erstellt"}</small>
                      </label>
                    </div>
                    <div className="editor-card">
                      <h3><Image /> Titelbild</h3>
                      <label className="upload-button">
                        <Image /> {uploading ? "Bild wird hochgeladen…" : "Bild auswählen"}
                        <input type="file" accept="image/*" disabled={uploading} onChange={(event) => void uploadCover(event)} />
                      </label>
                      <span className="editor-or">oder Bild-URL verwenden</span>
                      <label>
                        Bild-URL
                        <input value={editor.cover_image || ""} onChange={(event) => setEditor({ ...editor, cover_image: event.target.value || null })} placeholder="/images/beispiel.jpeg" />
                      </label>
                      {editor.cover_image && <img className="editor-preview-image" src={editor.cover_image} alt="Vorschau" />}
                    </div>
                  </aside>
                </div>
              </fieldset>
              <div className="editor-footer">
                <button type="button" className="admin-secondary-button" onClick={() => { setEditor(null); setSlugManuallyEdited(false); }}>Abbrechen</button>
                <button className="admin-primary-button" disabled={busy || uploading}>{editor.status === "published" ? <Send /> : editor.status === "archived" ? <ArchiveRestore /> : <Edit3 />}{busy ? "Wird gespeichert…" : "Beitrag speichern"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {pendingDelete && <AdminConfirmDialog title="Beitrag löschen?" description="Soll dieser Beitrag wirklich vollständig aus der News-Verwaltung gelöscht werden?" subject={`„${pendingDelete.title}“`} confirmLabel="Beitrag löschen" busy={deleteBusy} onCancel={() => setPendingDelete(null)} onConfirm={() => remove(pendingDelete)} />}
    </>
  );
}
